import React, { useCallback, useState } from 'react';

import FilterListIcon from '@mui/icons-material/FilterList';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {
  Button,
  Checkbox,
  Chip,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import PlaceLookUp from '~/components/Common/Forms/PlaceLookUp';
import { ALL_PLACE_TYPES, DEFAULT_ACTIVE_TYPES, PlaceType } from '~/components/Content/PolygonMerge/constants';

import { TypeaheadSearchResult } from '~/services/SearchService';

function geoTypeLabel(result: TypeaheadSearchResult): string {
  return result.geoType ?? result.destinationType ?? result.searchType;
}

function renderPlaceOption(props: React.HTMLAttributes<HTMLLIElement>, option: TypeaheadSearchResult) {
  return (
    <li {...props}>
      <Typography>
        {option.format.mainText}{' '}
        <Typography component="span" variant="body2" color="text.secondary">
          {option.format.secondaryText}
        </Typography>{' '}
        <Typography
          component="span"
          variant="caption"
          sx={{ backgroundColor: 'action.selected', borderRadius: '4px', px: 0.5, py: 0.25 }}
        >
          {geoTypeLabel(option)}
        </Typography>
      </Typography>
    </li>
  );
}

interface Props {
  onPreview: (targetPlaceId: string, sourcePlaceId: string) => void;
  onTargetChange: (placeId: string | null) => void;
  onSourceChange: (placeId: string | null) => void;
  onSwap: () => void;
  isLoading: boolean;
}

export default function PolygonMergeForm({ onPreview, onTargetChange, onSourceChange, onSwap, isLoading }: Props) {
  const [targetPlace, setTargetPlace] = useState<TypeaheadSearchResult | undefined>(undefined);
  const [sourcePlace, setSourcePlace] = useState<TypeaheadSearchResult | undefined>(undefined);
  const [activeTypes, setActiveTypes] = useState<Set<PlaceType>>(new Set(DEFAULT_ACTIVE_TYPES));
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);

  const types = Array.from(activeTypes);

  const toggleType = useCallback((type: PlaceType) => {
    setActiveTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const handleTargetChange = useCallback(
    (selection: TypeaheadSearchResult | undefined) => {
      setTargetPlace(selection);
      onTargetChange(selection?.id ?? null);
    },
    [onTargetChange],
  );

  const handleSourceChange = useCallback(
    (selection: TypeaheadSearchResult | undefined) => {
      setSourcePlace(selection);
      onSourceChange(selection?.id ?? null);
    },
    [onSourceChange],
  );

  const handleSwap = useCallback(() => {
    setTargetPlace(sourcePlace);
    setSourcePlace(targetPlace);
    onTargetChange(sourcePlace?.id ?? null);
    onSourceChange(targetPlace?.id ?? null);
    onSwap();
  }, [targetPlace, sourcePlace, onTargetChange, onSourceChange, onSwap]);

  const handlePreview = useCallback(() => {
    if (targetPlace && sourcePlace) {
      onPreview(targetPlace.id, sourcePlace.id);
    }
  }, [targetPlace, sourcePlace, onPreview]);

  const isSamePlace = !!(targetPlace && sourcePlace && targetPlace.id === sourcePlace.id);
  const canPreview = !!targetPlace && !!sourcePlace && !isLoading && !isSamePlace;

  return (
    <Stack direction="column" gap={3}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<FilterListIcon />}
        onClick={(e) => setFilterAnchor(e.currentTarget)}
        sx={{ alignSelf: 'flex-start' }}
      >
        Filter ({activeTypes.size}/{ALL_PLACE_TYPES.length})
      </Button>
      <Menu anchorEl={filterAnchor} open={Boolean(filterAnchor)} onClose={() => setFilterAnchor(null)}>
        <MenuItem
          dense
          onClick={() => {
            const allSelected = activeTypes.size === ALL_PLACE_TYPES.length;
            setActiveTypes(allSelected ? new Set() : new Set(ALL_PLACE_TYPES));
          }}
        >
          <Checkbox
            checked={activeTypes.size === ALL_PLACE_TYPES.length}
            indeterminate={activeTypes.size > 0 && activeTypes.size < ALL_PLACE_TYPES.length}
            size="small"
            sx={{ p: 0, mr: 1 }}
          />
          <ListItemText primary={activeTypes.size === ALL_PLACE_TYPES.length ? 'Deselect all' : 'Select all'} />
        </MenuItem>
        {ALL_PLACE_TYPES.map((type) => (
          <MenuItem key={type} dense onClick={() => toggleType(type)}>
            <Checkbox checked={activeTypes.has(type)} size="small" sx={{ p: 0, mr: 1 }} />
            <ListItemText primary={type} />
          </MenuItem>
        ))}
      </Menu>

      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ md: 'flex-start' }}>
        <Stack direction="column" gap={1} flex={1}>
          <PlaceLookUp
            onCurrentValueChange={handleTargetChange}
            types={types}
            label="Expand this place"
            required={false}
            renderOption={renderPlaceOption}
          />
          {targetPlace && (
            <Stack direction="row" gap={1} alignItems="center">
              <Chip label={geoTypeLabel(targetPlace)} size="small" color="primary" variant="outlined" />
              <Typography variant="body2" color="text.secondary">
                {targetPlace.format.secondaryText}
              </Typography>
            </Stack>
          )}
        </Stack>

        <Tooltip title="Swap target and source">
          <IconButton size="small" onClick={handleSwap} disabled={!targetPlace && !sourcePlace} sx={{ mt: { md: 1 } }}>
            <SwapHorizIcon />
          </IconButton>
        </Tooltip>

        <Stack direction="column" gap={1} flex={1}>
          <PlaceLookUp
            onCurrentValueChange={handleSourceChange}
            types={types}
            label="Include area from"
            required={false}
            renderOption={renderPlaceOption}
          />
          {sourcePlace && (
            <Stack direction="row" gap={1} alignItems="center">
              <Chip label={geoTypeLabel(sourcePlace)} size="small" color="success" variant="outlined" />
              <Typography variant="body2" color="text.secondary">
                {sourcePlace.format.secondaryText}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>

      <Stack direction="row" gap={2} alignItems="center">
        <Button variant="contained" color="primary" onClick={handlePreview} disabled={!canPreview}>
          Preview
        </Button>
        {isSamePlace && (
          <Typography variant="body2" color="error.main">
            Both places must be different.
          </Typography>
        )}
      </Stack>
    </Stack>
  );
}
